import React, { useCallback, useState } from 'react';

// Styles & Assets
import styles from './CalendarMonth.module.css';

// Services
import { getMomentJSDaysWeek, getMonthsYear } from '../../../services/getPollAttribute';

// Hooks
import { useTranslation } from 'react-i18next';

// Components
import CalendarEvent from '../CalendarEvent/CalendarEvent';
import Modal from '../../UI/Modal/Modal';
import DayMonth from '../DayMonth/DayMonth';

// Other
import moment from 'moment';
import EventSummary from '../EventSummary/EventSummary';

// Interfaces
interface Props {
    month: number
    year: number
    clickedDay: moment.Moment | null
    onClickDay: (day: moment.Moment) => () => void
}

const CalendarMonth: React.FC<Props> = ({ month, year, clickedDay, onClickDay }) => {

    const { t } = useTranslation();

    const firstDayMonth = moment(`${month + 1}-${year}`, 'MM-YYYY').clone().startOf('month');

    const getNumDaysPastMonth = useCallback((dayOfWeek: number) => {
        if (dayOfWeek === 0) return 6;
        else return dayOfWeek - 1;
    }, []);

    const setCalendar = useCallback(() => {

        const dayOfWeek = firstDayMonth.day();
        const daysPreviousMonth = getNumDaysPastMonth(dayOfWeek);

        const newCalendar: moment.Moment[] = [];

        for (let i = 0; i < daysPreviousMonth; i++) {
            newCalendar.push(firstDayMonth.clone().subtract(daysPreviousMonth - i, 'd'));
        }

        newCalendar.push(firstDayMonth);
        const daysInCurrentMonth = firstDayMonth.daysInMonth();

        for (let i = 1; i < daysInCurrentMonth; i++) {
            newCalendar.push(newCalendar[newCalendar.length - 1].clone().add(1, 'day'));
        }

        const daysNextMonth = 7 * 6 - daysInCurrentMonth - daysPreviousMonth;

        for (let i = 0; i < daysNextMonth; i++) {
            newCalendar.push(newCalendar[newCalendar.length - 1].clone().add(1, 'day'));
        }

        return newCalendar;

    }, [getNumDaysPastMonth, firstDayMonth]);

    const calendar = setCalendar();

    const getDayLabel = (weekDay: number) => t(getMomentJSDaysWeek().find(d => d.id === weekDay)?.label ?? '');
    const getMonthLabel = (month: number) => t(getMonthsYear().find(m => m.id === month)?.label ?? '');

    const [handleOpenEvent, setHandleOpenEvent] = useState(false);

    return (
        <div className={styles.calendarMonth}>
            <div className={styles.calendarContainer}>
                <div className={styles.weekDays}>
                    {getMomentJSDaysWeek().map(d =>
                        <p key={d.id}>{t(d.label)}</p>
                    )}
                </div>
                <div className={styles.calendar}>
                    {calendar.map(day =>
                        <DayMonth
                            key={day.unix()}
                            day={day}
                            selectedDay={clickedDay !== null && clickedDay.isSame(day, 'day')}
                            thisMonth={day.month() === month}
                            onclickDay={onClickDay}
                        />
                    )}
                </div>
            </div>
            {clickedDay &&
                <div className={styles.clickedDay}>
                    <p className={styles.labelClickedDay}>{getDayLabel(clickedDay.day())} {clickedDay.date()} {getMonthLabel(clickedDay.month())}</p>
                    <div className={styles.datesContainer}>
                        <EventSummary onClick={() => setHandleOpenEvent(true)} />
                        <Modal show={handleOpenEvent} setShowModal={setHandleOpenEvent}>
                            <CalendarEvent />
                        </Modal>
                    </div>
                </div>
            }
        </div>
    );
};

export default CalendarMonth;